import { axiosInstance } from "@/lib/axios-instance";
import type { CompanyInfoFormData } from "@/lib/schema/submission-schema";
import { BUSINESS_TYPE_MAPPING } from "@/lib/schema/submission-schema";
import { useAuthStore } from "@/store/auth-store";
import { getErrorMessage } from "@/lib/api-error";

const getBusinessModelValue = (readableFormat: string): string => {
  if (!readableFormat) return readableFormat;

  const shortFormats = Object.values(BUSINESS_TYPE_MAPPING);
  if (shortFormats.includes(readableFormat)) {
    return readableFormat;
  }

  if (BUSINESS_TYPE_MAPPING[readableFormat]) {
    return BUSINESS_TYPE_MAPPING[readableFormat];
  }

  const normalizedInput = readableFormat.toLowerCase().trim();
  for (const [key, value] of Object.entries(BUSINESS_TYPE_MAPPING)) {
    if (key.toLowerCase() === normalizedInput) {
      return value;
    }
  }

  if (
    normalizedInput.includes("b2b") ||
    normalizedInput.includes("business to business")
  ) {
    return "B2B";
  }
  if (
    normalizedInput.includes("b2c") ||
    normalizedInput.includes("business to consumer") ||
    normalizedInput.includes("business to customer")
  ) {
    return "B2C";
  }
  if (
    normalizedInput.includes("b2g") ||
    normalizedInput.includes("business to government")
  ) {
    return "B2G";
  }
  if (
    normalizedInput.includes("c2c") ||
    normalizedInput.includes("consumer to consumer") ||
    normalizedInput.includes("marketplace")
  ) {
    return "C2C";
  }

  return readableFormat;
};

// Normalises an applicant-provided business-model selection (now multi-select
// on the portal) into an array of short-format codes the backend stores.
const normalizeBusinessModels = (
  input: string | string[] | undefined,
): string[] => {
  const items = Array.isArray(input) ? input : input ? [input] : [];
  const mapped = items
    .map((v) => (typeof v === "string" ? getBusinessModelValue(v) : ""))
    .filter((v) => !!v);
  // De-duplicate while preserving order.
  return Array.from(new Set(mapped));
};

// Company related APIs
export const companyApi = {
  // Update company information (matches deals-api.ts pattern)
  updateCompany: async (
    companyId: string,
    companyData: CompanyInfoFormData
  ) => {
    try {
      // Convert each selected business model into its short-format code.
      const businessModelValue = normalizeBusinessModels(
        companyData.businessModel,
      );
      // Revenue model is now a multi-select; values are the option keys
      // already, so just coerce into an array of non-empty strings.
      const revenueModelValue = Array.isArray(companyData.revenueModel)
        ? companyData.revenueModel.filter((v): v is string => typeof v === "string" && v.length > 0)
        : companyData.revenueModel
          ? [companyData.revenueModel]
          : [];

      // Normalize GICS fields to arrays
      const gicsIndustry = Array.isArray(companyData.gicsIndustry)
        ? companyData.gicsIndustry
        : companyData.gicsIndustry ? [companyData.gicsIndustry] : [];
      const gicsSubIndustry = Array.isArray(companyData.gicsSubIndustry)
        ? companyData.gicsSubIndustry
        : companyData.gicsSubIndustry ? [companyData.gicsSubIndustry] : [];

      console.log("📤 Sending company update:", {
        contact_phone: companyData.contactPhone || "",
        company_website: companyData.companyWebsite || "",
        linkedIn: companyData.linkedInUrl || "",
        stage: companyData.companyStage,
        address: companyData.address,
        country: companyData.country,
        // GICS hierarchy (2-level: Industry + Sub-Industry)
        gics_industry: gicsIndustry,
        gics_sub_industry: gicsSubIndustry,
        // Business & Revenue
        business_model: businessModelValue,
        revenue_model: revenueModelValue,
        original_businessModel: companyData.businessModel,
      });

      const response = await axiosInstance.post(
        `/edit_company_during_application?company_id=${companyId}`,
        {
          contact_phone: companyData.contactPhone || "",
          company_website: companyData.companyWebsite || "",
          linkedIn: companyData.linkedInUrl || "",
          stage: companyData.companyStage,
          address: companyData.address,
          country: companyData.country,
          // GICS hierarchy (2-level: Industry + Sub-Industry)
          gics_industry: gicsIndustry,
          gics_sub_industry: gicsSubIndustry,
          // Business & Revenue
          business_model: businessModelValue,
          revenue_model: revenueModelValue,
        }
      );

      // Validate response status
      if (response.data.status === "error") {
        throw new Error(
          response.data.message || "Failed to update company information"
        );
      }

      return response.data;
    } catch (error: any) {
      console.error("Error updating company:", error);
      throw new Error(
        getErrorMessage(error, "Failed to update company information")
      );
    }
  },
};

// Application submission APIs
export const applicationApi = {
  // Check if user has already applied to an opportunity
  checkApplicationStatus: async (opportunityId: string, userId: string) => {
    try {
      const response = await axiosInstance.get(
        `/check_application_status?opportunity_id=${opportunityId}&user_id=${userId}`
      );

      if (response.data.status === "error") {
        throw new Error(
          response.data.message || "Failed to check application status"
        );
      }

      return response.data;
    } catch (error: any) {
      console.error("Error checking application status:", error);
      throw new Error(
        getErrorMessage(error, "Failed to check application status")
      );
    }
  },

  // Get application data by company ID and opportunity ID
  getApplicationData: async (companyId: string, opportunityId: string) => {
    try {
      const response = await axiosInstance.get(
        `/get_application_data?company_id=${companyId}&opportunity_id=${opportunityId}`
      );

      if (response.data.status === "error") {
        throw new Error(
          response.data.message || "Failed to fetch application data"
        );
      }

      return response.data;
    } catch (error: any) {
      console.error("Error fetching application data:", error);
      throw new Error(
        getErrorMessage(error, "Failed to fetch application data")
      );
    }
  },

  // Update application data by application ID
  updateApplicationData: async (
    applicationId: string,
    applicationData: any
  ) => {
    try {
      const response = await axiosInstance.put(
        `/update_application_data/${applicationId}`,
        applicationData
      );

      if (response.data.status === "error") {
        throw new Error(
          response.data.message || "Failed to update application data"
        );
      }

      return response.data;
    } catch (error: any) {
      console.error("Error updating application data:", error);
      throw new Error(
        getErrorMessage(error, "Failed to update application data")
      );
    }
  },
  // Submit application to opportunity with application data
  submitApplication: async (
    companyId: string,
    opportunityId: string,
    applicationData?: any,
    userId?: string // Allow passing userId directly (for Clerk users)
  ) => {
    // Try to get user ID from parameter first, then from auth store
    let finalUserId = userId;

    if (!finalUserId) {
      const user = useAuthStore.getState().user;
      finalUserId = user?.id;
    }

    // If still no user ID, try to get from Clerk
    if (!finalUserId && window.Clerk?.user) {
      finalUserId = window.Clerk.user.id;
    }

    if (!finalUserId) {
      throw new Error("User must be logged in to submit application");
    }

    // Validate required parameters
    if (!companyId || !opportunityId) {
      throw new Error("Company ID and Opportunity ID are required");
    }

    console.log("Submitting application with:", {
      companyId,
      opportunityId,
      userId: finalUserId,
      applicationData: applicationData ? "present" : "not present",
    });

    try {
      const requestBody = {
        applicant_company: companyId,
        pertains_to_opportunity: opportunityId,
        changed_by: finalUserId, // Use the resolved user ID
        ...applicationData, // Include any additional application data
        source: "Portal",
      };

      const response = await axiosInstance.post(
        "/create_opportunity_application",
        requestBody
      );

      // Validate response
      if (response.data.status === "error") {
        throw new Error(
          response.data.message || "Application submission failed"
        );
      }

      // If application already exists, fetch it and return it so we can update it
      if (response.data.status === "exists") {
        console.log(
          "ℹ️ Application already exists, fetching existing application..."
        );
        try {
          // Fetch the existing application data using the same API
          const existingAppResponse = await axiosInstance.get(
            `/get_application_data?company_id=${companyId}&opportunity_id=${opportunityId}`
          );

          if (existingAppResponse.data.status === "error") {
            throw new Error(
              existingAppResponse.data.message ||
              "Failed to fetch existing application"
            );
          }

          console.log(
            "✅ Fetched existing application:",
            existingAppResponse.data
          );
          return {
            ...existingAppResponse.data,
            status: "exists",
            message: "Application already exists, fetched existing data",
          };
        } catch (fetchError) {
          console.error("Error fetching existing application:", fetchError);
          // If we can't fetch it, still throw the exists error
          throw new Error("Application already exists for this opportunity");
        }
      }

      console.log("Application submitted successfully:", response.data);
      return response.data;
    } catch (error: any) {
      console.error("Application submission error:", error);
      throw new Error(
        getErrorMessage(
          error,
          "Failed to submit application. Please try again."
        )
      );
    }
  },


};

// LinkedIn scraper API
export const linkedInScraperApi = {
  scrapeLinkedIn: async (
    url: string,
    companyId: string,
    opportunityId: string
  ) => {
    try {
      const response = await axiosInstance.post(
        "/linkedin_scrape_company",
        {
          url,
        },
        {
          params: {
            company_id: companyId,
            opportunity_id: opportunityId,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("Error calling LinkedIn scraper:", error);
      // Don't throw - this is a fire-and-forget async call
      return null;
    }
  },
};

// Export all APIs
export const submissionApiClient = {
  company: companyApi,
  application: applicationApi,
  linkedInScraper: linkedInScraperApi,
};

export default submissionApiClient;
